import React, { useCallback, useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import RunningTestCard from "./running-test-card";
import ProposalCard from "./proposal-card";
import HistoryTable from "./history-table";
import { PageHeading } from "../common-styling";
import { SectionTitle, EmptyState, TestCard } from "./split-test.styles";
import TableLoader from "../loader/table-loader";
import { useDialog } from "../../hooks/useDialog";
import { showError, showSuccess, showInfo } from "../../services/toast-service";
import {
  fetchSplitTestOverview,
  proposeSplitTest,
  createSplitTest,
  updateSplitTest,
  startSplitTest,
  cancelSplitTest,
  evaluateSplitTest,
  rolloutSplitTest,
  revertSplitTest
} from "../../services/api-declaration";
import { SPLIT_TEST_STATUS } from "../strings";

// One funnel type (LEAD / EMPLOYEE): running test, next proposal, history.
const SplitTestDashboard = ({ funnelType }) => {
  const { t } = useTranslation();
  const { showConfirmationDialogBox } = useDialog();
  const [isLoading, setIsLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [overview, setOverview] = useState(null);

  const load = useCallback(async () => {
    try {
      const resp = await fetchSplitTestOverview({ funnelType });
      setOverview(resp.data.data);
    } catch (error) {
      showError(
        error?.response?.data?.message ||
          t("toastMessage.errorInGetting", { what: t("splitTests.title") })
      );
    } finally {
      setIsLoading(false);
    }
  }, [funnelType, t]);

  useEffect(() => {
    setIsLoading(true);
    load();
  }, [load]);

  const run = async (action, successMsg) => {
    setBusy(true);
    try {
      const result = await action();
      if (successMsg) showSuccess(successMsg);
      await load();
      return result;
    } catch (error) {
      showError(error?.response?.data?.message || error.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const confirm = ({
    title,
    message,
    acceptLabel,
    onAccept,
    dialogType = "warning"
  }) =>
    showConfirmationDialogBox({
      title,
      dialogMessage: message,
      dialogType,
      acceptLabel,
      declineLabel: t("common.cancel", { defaultValue: "Abbrechen" }),
      responseAction: (accepted) => {
        if (accepted) onAccept();
      }
    });

  const running = overview?.running;
  const proposal = overview?.proposals?.[0] || null;
  const elements = overview?.elements || [];
  const elementLabel = (key) =>
    elements.find((e) => e.key === key)?.label || key;

  const handlePropose = () =>
    run(() => proposeSplitTest({ funnelType }), t("splitTests.toast.proposed"));

  const handleStart = (item) =>
    confirm({
      title: t("splitTests.confirm.startTitle"),
      message: t("splitTests.confirm.startText", {
        element: elementLabel(item.element),
        count: item.eligibleFunnelCount ?? 0
      }),
      acceptLabel: t("splitTests.actions.start"),
      dialogType: "info",
      onAccept: () =>
        run(() => startSplitTest(item._id), t("splitTests.toast.started"))
    });

  const handleCancel = () =>
    confirm({
      title: t("splitTests.confirm.cancelTitle"),
      message: t("splitTests.confirm.cancelText"),
      acceptLabel: t("splitTests.actions.cancel"),
      onAccept: () =>
        run(() => cancelSplitTest(running._id), t("splitTests.toast.cancelled"))
    });

  const handleEvaluate = async () => {
    const result = await run(() => evaluateSplitTest(running._id));
    const status = result?.data?.data?.status;
    if (status === SPLIT_TEST_STATUS.RUNNING) {
      showInfo(t("splitTests.toast.stillRunning"));
    } else if (status) {
      showSuccess(
        t("splitTests.toast.decided", {
          status: t(`splitTests.status.${status}`)
        })
      );
    }
  };

  const handleRevert = (item) =>
    confirm({
      title: t("splitTests.confirm.revertTitle"),
      message: t("splitTests.confirm.revertText", {
        funnels: item.rollout?.funnelsUpdated ?? 0
      }),
      acceptLabel: t("splitTests.actions.revert"),
      onAccept: () =>
        run(() => revertSplitTest(item._id), t("splitTests.toast.reverted"))
    });

  // Retry of a failed automatic roll-out (won test without rolledOutAt)
  const handleRollout = (item) =>
    confirm({
      title: t("splitTests.confirm.rolloutTitle"),
      message: t("splitTests.confirm.rolloutText"),
      acceptLabel: t("splitTests.actions.retryRollout"),
      dialogType: "info",
      onAccept: async () => {
        const result = await run(() => rolloutSplitTest(item._id));
        const rollout = result?.data?.data?.rollout;
        if (rollout) {
          showSuccess(
            t("splitTests.toast.rolledOut", {
              funnels: rollout.funnelsUpdated ?? 0,
              templates: rollout.templatesUpdated ?? 0
            })
          );
        }
      }
    });

  const handleSave = (id, form) =>
    run(() => updateSplitTest(id, form), t("splitTests.toast.saved"));
  const handleCreateManual = (form) =>
    run(
      () => createSplitTest({ ...form, funnelType }),
      t("splitTests.toast.created")
    );

  return (
    <div className="pb-4">
      <PageHeading>
        {t("splitTests.title")} ·{" "}
        {t(`splitTests.tabs.${funnelType.toLowerCase()}`)}
      </PageHeading>
      <div className="text-muted mb-3 ms-1">
        <Trans i18nKey="splitTests.intro" />
      </div>

      {isLoading ? (
        <TestCard className="p-3">
          <table className="w-100">
            <tbody>
              <TableLoader colSpan={1} />
            </tbody>
          </table>
        </TestCard>
      ) : (
        <>
          <SectionTitle>{t("splitTests.running.title")}</SectionTitle>
          {running ? (
            <RunningTestCard
              test={running}
              elementLabel={elementLabel(running.element)}
              elementType={
                elements.find((e) => e.key === running.element)?.type || "text"
              }
              busy={busy}
              onEvaluate={handleEvaluate}
              onCancel={handleCancel}
            />
          ) : (
            <TestCard className="p-3 mb-4">
              <EmptyState>
                <div className="title">
                  {t("splitTests.running.emptyTitle")}
                </div>
                <div>{t("splitTests.running.emptyText")}</div>
              </EmptyState>
            </TestCard>
          )}

          <SectionTitle>{t("splitTests.proposal.section")}</SectionTitle>
          <ProposalCard
            proposal={proposal}
            elements={elements}
            funnelType={funnelType}
            hasRunning={Boolean(running)}
            busy={busy}
            onPropose={handlePropose}
            onSave={handleSave}
            onStart={handleStart}
            onCreateManual={handleCreateManual}
          />

          <SectionTitle>{t("splitTests.history.title")}</SectionTitle>
          <HistoryTable
            history={overview?.history || []}
            elements={elements}
            busy={busy}
            onRevert={handleRevert}
            onRollout={handleRollout}
          />
        </>
      )}
    </div>
  );
};

export default SplitTestDashboard;
